import React, { useEffect, useMemo, useState } from 'react';
import api from '../../config/axios';

const REPORT_PERIODS = [60, 90, 120, 180, 365];

const apiPath = path => {
  const baseUrl = String(api.defaults.baseURL || '').replace(/\/$/, '');
  return `${baseUrl.endsWith('/api') ? '' : '/api'}${path}`;
};

const formatDate = value => value
  ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

const cloneContent = content => ({
  executive_overview: content.executive_overview || '',
  completed_work_summary: content.completed_work_summary || '',
  completed_highlights: (content.completed_highlights || []).map(item => ({ ...item, included: true })),
  recommended_attention_summary: content.recommended_attention_summary || '',
  recommended_attention_items: (content.recommended_attention_items || []).map(item => ({ ...item, included: true })),
});

const downloadErrorMessage = async (error, fallback) => {
  if (error.response?.data instanceof Blob) {
    try {
      const payload = JSON.parse(await error.response.data.text());
      return payload.message || fallback;
    } catch (_) {
      return fallback;
    }
  }
  return error.response?.data?.message || error.message || fallback;
};

const OwnerWorkOrderLinks = ({ ids, referenceMap, onOpen }) => {
  if (!ids?.length) return <span className="text-xs text-gray-400">Manually added item</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {ids.map(id => {
        const order = referenceMap.get(id);
        return (
          <button
            type="button"
            key={id}
            onClick={() => onOpen(id)}
            className="rounded bg-gray-100 px-2 py-1 text-left text-xs font-medium text-sky-700 hover:bg-sky-100"
            title={order?.description || 'Open work order'}
          >
            {formatDate(order?.completedAt || order?.createdAt)} · {order?.description?.slice(0, 48) || `Work order ${id.slice(0, 8)}`}
            {order?.description?.length > 48 ? '…' : ''}
          </button>
        );
      })}
    </div>
  );
};

const ReportItemEditor = ({
  title,
  items,
  field,
  onChange,
  referenceMap,
  onOpenWorkOrder,
}) => {
  const updateItem = (index, changes) => {
    const next = items.map((item, itemIndex) => itemIndex === index ? { ...item, ...changes } : item);
    onChange(field, next);
  };
  const moveItem = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(field, next);
  };
  const removeItem = index => onChange(field, items.filter((_, itemIndex) => itemIndex !== index));
  const addItem = () => onChange(field, [
    ...items,
    { title: '', detail: '', work_order_ids: [], included: true },
  ]);

  return (
    <section>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <button type="button" onClick={addItem} className="rounded-md border border-emerald-300 px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-50">+ Add item</button>
      </div>
      <div className="mt-3 space-y-3">
        {items.map((item, index) => (
          <article key={`${field}-${index}`} className={`rounded-lg border p-4 ${item.included ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-70'}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={item.included !== false} onChange={event => updateItem(index, { included: event.target.checked })} className="h-4 w-4 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600" />
                Include in owner report
              </label>
              <div className="flex gap-1">
                <button type="button" onClick={() => moveItem(index, -1)} disabled={index === 0} aria-label="Move item up" className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 disabled:opacity-30">↑</button>
                <button type="button" onClick={() => moveItem(index, 1)} disabled={index === items.length - 1} aria-label="Move item down" className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 disabled:opacity-30">↓</button>
                <button type="button" onClick={() => removeItem(index)} className="rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50">Delete</button>
              </div>
            </div>
            <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Title
              <input
                value={item.title}
                maxLength={160}
                onChange={event => updateItem(index, { title: event.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </label>
            <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Detail
              <textarea
                value={item.detail}
                maxLength={900}
                rows={3}
                onChange={event => updateItem(index, { detail: event.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </label>
            <div className="mt-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Internal source — excluded from PDF</div>
              <OwnerWorkOrderLinks ids={item.work_order_ids} referenceMap={referenceMap} onOpen={onOpenWorkOrder} />
            </div>
          </article>
        ))}
        {!items.length && <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">No items in this section. You can add one manually.</div>}
      </div>
    </section>
  );
};

const PreviewItems = ({ items, emptyText, color }) => {
  const included = items.filter(item => item.included !== false);
  if (!included.length) return <p className="mt-3 text-sm italic text-gray-500">{emptyText}</p>;
  return (
    <div className="mt-4 space-y-3">
      {included.map((item, index) => (
        <article key={`${item.title}-${index}`} className={`rounded-lg border-l-4 p-4 ${color}`}>
          <h4 className="font-semibold text-gray-900">{item.title || 'Untitled item'}</h4>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-gray-700">{item.detail}</p>
        </article>
      ))}
    </div>
  );
};

export default function OwnerReportEditor({
  propertyId,
  property,
  completedDays,
  onOpenWorkOrder,
}) {
  const [reportDays, setReportDays] = useState(completedDays);
  const [draft, setDraft] = useState(null);
  const [content, setContent] = useState(null);
  const [mode, setMode] = useState('edit');
  const [includeCompletedAppendix, setIncludeCompletedAppendix] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const referenceMap = useMemo(
    () => new Map((draft?.workOrderReferences || []).map(order => [order.id, order])),
    [draft],
  );

  useEffect(() => {
    if (!draft) return undefined;
    const warning = 'This owner report draft is not saved. If you leave this page, you will need to generate it again.';
    const beforeUnload = event => {
      event.preventDefault();
      event.returnValue = warning;
      return warning;
    };
    const captureNavigation = event => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || event.defaultPrevented) return;
      const destination = new URL(anchor.href, window.location.href);
      if (destination.href === window.location.href) return;
      if (!window.confirm(warning)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', captureNavigation, true);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', captureNavigation, true);
    };
  }, [draft]);

  const updateContent = (field, value) => setContent(current => ({ ...current, [field]: value }));

  const generateDraft = async () => {
    if (draft && !window.confirm('Generate a new owner briefing? Your current unsaved edits will be permanently replaced.')) return;
    setError('');
    setGenerating(true);
    try {
      const response = await api.post(apiPath(`/property-performance/${propertyId}/owner-report/draft`), { completedDays: reportDays });
      setDraft(response.data);
      setContent(cloneContent(response.data.content));
      setIncludeCompletedAppendix(true);
      setMode('edit');
    } catch (generationError) {
      setError(await downloadErrorMessage(generationError, 'Unable to generate the owner briefing'));
    } finally {
      setGenerating(false);
    }
  };

  const changePeriod = event => {
    const nextDays = Number(event.target.value);
    if (draft && !window.confirm('Changing the reporting period will discard the current unsaved draft. Continue?')) return;
    setDraft(null);
    setContent(null);
    setError('');
    setReportDays(nextDays);
  };

  const discardDraft = () => {
    if (!window.confirm('Discard this unsaved owner report draft? This cannot be undone.')) return;
    setDraft(null);
    setContent(null);
    setError('');
    setMode('edit');
  };

  const pdfContent = () => ({
    executive_overview: content.executive_overview,
    completed_work_summary: content.completed_work_summary,
    completed_highlights: content.completed_highlights
      .filter(item => item.included !== false)
      .map(({ included, ...item }) => item),
    recommended_attention_summary: content.recommended_attention_summary,
    recommended_attention_items: content.recommended_attention_items
      .filter(item => item.included !== false)
      .map(({ included, ...item }) => item),
  });

  const validateForDownload = () => {
    const requiredSections = [
      ['Executive overview', content.executive_overview],
      ['Completed-work summary', content.completed_work_summary],
      ['Recommended-attention summary', content.recommended_attention_summary],
    ];
    const missingSection = requiredSections.find(([, value]) => !value.trim());
    if (missingSection) return `${missingSection[0]} cannot be empty.`;
    const includedItems = [...content.completed_highlights, ...content.recommended_attention_items]
      .filter(item => item.included !== false);
    if (includedItems.some(item => !item.title.trim() || !item.detail.trim())) {
      return 'Every included report item needs both a title and detail. Complete it, exclude it, or delete it before downloading.';
    }
    return '';
  };

  const downloadPdf = async () => {
    setError('');
    const validationError = validateForDownload();
    if (validationError) {
      setError(validationError);
      setMode('edit');
      return;
    }
    setDownloading(true);
    try {
      const response = await api.post(
        apiPath(`/property-performance/${propertyId}/owner-report/pdf`),
        { completedDays: draft.completedDays, periodEnd: draft.periodEnd, includeCompletedAppendix, content: pdfContent() },
        { responseType: 'blob' },
      );
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fallback = `${property.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-property-care-update.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filenameMatch?.[1] || fallback;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(await downloadErrorMessage(downloadError, 'Unable to download the owner report'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="mt-4 rounded-lg border border-emerald-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Reporting Summary</div>
          <h2 className="mt-1 text-xl font-semibold text-gray-900">Owner property care update</h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">Generate an owner-facing briefing, verify and edit every section, preview it, and then download the final PDF. Internal scores, reviews, costs, hours, and staff notes are excluded.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Reporting period
            <select value={reportDays} onChange={changePeriod} disabled={generating} className="mt-1 block rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-900">
              {REPORT_PERIODS.map(days => <option key={days} value={days}>Last {days} days</option>)}
            </select>
          </label>
          <button type="button" onClick={generateDraft} disabled={generating || downloading} className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-gray-300">
            {generating ? 'Generating briefing…' : draft ? 'Regenerate briefing' : 'Generate owner briefing'}
          </button>
        </div>
      </div>

      {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {!draft && !generating && (
        <div className="mt-5 rounded-lg border border-dashed border-emerald-300 bg-emerald-50/50 p-8 text-center">
          <h3 className="font-semibold text-gray-900">No owner briefing generated</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-gray-600">Generation happens only when requested for this property. The draft will exist only in this browser page and will not be saved.</p>
        </div>
      )}

      {draft && content && (
        <div className="mt-5">
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <strong>This draft is not saved.</strong> Download the PDF before leaving this page. Refreshing, closing, or navigating away will permanently discard the draft and your edits.
          </div>

          <div className="mt-4 flex flex-col gap-3 rounded-lg bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-gray-500">Generated {formatDate(draft.generatedAt)} · {draft.completedWorkOrderCount} completed items · {draft.activeWorkOrderCount} active items</div>
            <div className="flex flex-wrap gap-2">
              <div className="inline-flex rounded-md border border-gray-300 bg-white p-1">
                <button type="button" onClick={() => setMode('edit')} className={`rounded px-3 py-1.5 text-sm font-medium ${mode === 'edit' ? 'bg-emerald-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Edit</button>
                <button type="button" onClick={() => setMode('preview')} className={`rounded px-3 py-1.5 text-sm font-medium ${mode === 'preview' ? 'bg-emerald-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Preview</button>
              </div>
              <button type="button" onClick={discardDraft} disabled={downloading} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">Discard</button>
              <button type="button" onClick={downloadPdf} disabled={downloading || generating} className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-gray-300">{downloading ? 'Preparing PDF…' : 'Download PDF'}</button>
            </div>
          </div>

          {mode === 'edit' ? (
            <div className="mt-5 space-y-6">
              <label className="block text-sm font-semibold text-gray-900">
                Executive overview
                <textarea value={content.executive_overview} maxLength={900} rows={5} onChange={event => updateContent('executive_overview', event.target.value)} className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
              </label>
              <label className="block text-sm font-semibold text-gray-900">
                Completed-work summary
                <textarea value={content.completed_work_summary} maxLength={1600} rows={5} onChange={event => updateContent('completed_work_summary', event.target.value)} className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
              </label>
              <ReportItemEditor title="Completed-work highlights" items={content.completed_highlights} field="completed_highlights" onChange={updateContent} referenceMap={referenceMap} onOpenWorkOrder={onOpenWorkOrder} />
              <label className="block text-sm font-semibold text-gray-900">
                Recommended-attention summary
                <textarea value={content.recommended_attention_summary} maxLength={1600} rows={5} onChange={event => updateContent('recommended_attention_summary', event.target.value)} className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-normal text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
              </label>
              <ReportItemEditor title="Recommended-attention items" items={content.recommended_attention_items} field="recommended_attention_items" onChange={updateContent} referenceMap={referenceMap} onOpenWorkOrder={onOpenWorkOrder} />
            </div>
          ) : (
            <div className="mx-auto mt-5 max-w-4xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <header className="bg-sky-950 px-7 py-8 text-white">
                <div className="text-sm font-semibold tracking-[0.2em] text-sky-200">SWEETGRASS PROPERTIES</div>
                <h3 className="mt-4 text-2xl font-semibold">Property Care Update</h3>
              </header>
              <div className="space-y-8 p-7">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{draft.propertyName}</h3>
                  <p className="mt-1 text-sm text-gray-500">{draft.address}</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-sky-700">{formatDate(draft.periodStart)} – {formatDate(draft.periodEnd)}</p>
                </div>
                <section><h3 className="text-lg font-semibold text-sky-950">Overview</h3><p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700">{content.executive_overview}</p></section>
                <section>
                  <h3 className="text-lg font-semibold text-sky-950">Completed Property Care</h3>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700">{content.completed_work_summary}</p>
                  <PreviewItems items={content.completed_highlights} emptyText="No completed-work highlights are included." color="border-emerald-600 bg-emerald-50" />
                </section>
                <section>
                  <h3 className="text-lg font-semibold text-sky-950">Recommended Attention</h3>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700">{content.recommended_attention_summary}</p>
                  <PreviewItems items={content.recommended_attention_items} emptyText="No current property-care items require owner attention based on the available records." color="border-amber-600 bg-amber-50" />
                </section>
                {includeCompletedAppendix && (
                  <details className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <summary className="cursor-pointer font-semibold text-gray-900">Completed Work Detail ({draft.completedWorkOrders.length})</summary>
                    <div className="mt-4 divide-y divide-gray-200">
                      {draft.completedWorkOrders.map(order => <div key={order.id} className="grid gap-1 py-3 text-sm sm:grid-cols-[120px_1fr]"><span className="font-medium text-emerald-700">{formatDate(order.completedAt)}</span><span className="text-gray-700">{order.description}</span></div>)}
                      {!draft.completedWorkOrders.length && <p className="py-3 text-sm text-gray-500">No completed work was recorded during this reporting period.</p>}
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}

          <label className="mt-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            <input type="checkbox" checked={includeCompletedAppendix} onChange={event => setIncludeCompletedAppendix(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-700 focus:ring-emerald-600" />
            <span><strong className="block text-gray-900">Include completed-work appendix</strong>Includes all {draft.completedWorkOrderCount} completed work orders from the selected reporting period. Enabled by default.</span>
          </label>
        </div>
      )}
    </section>
  );
}
